// API configuration utility
export const getApiBaseUrl = () => {
    console.log("\n\nWTFF: ", window.location.hostname, "\n\n")
    // Check if we're running in development (localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:8787';
    }
    // In production, use relative paths
    return 'https://barrel.parkeragee.com';
};

export const getWebSocketBaseUrl = () => {
    // Check if we're running in development (localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'ws://localhost:8787';
    }
    // In production, use secure WebSocket with relative path
    return 'wss://barrel.parkeragee.com';
};

// Logging utility
export const logger = {
    log: (...args) => {
        if (process.env.NODE_ENV !== 'production') {
            console.log(...args);
        }
    },
    error: (...args) => {
        // Always log errors
        console.error(...args);
    }
};

export class Api {
    constructor() {
        this.baseUrl = getApiBaseUrl();
    }

    async getAllEvents() {
        const response = await fetch(`${this.baseUrl}/api/events`);
        if (!response.ok) {
            throw new Error(`Failed to fetch events: ${response.statusText}`);
        }
        return response.json();
    }

    async getEvent(eventId) {
        const response = await fetch(`${this.baseUrl}/api/events/${eventId}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch event: ${response.statusText}`);
        }
        return response.json();
    }

    async createEvent(details) {
        const response = await fetch(`${this.baseUrl}/api/events`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(details),
        });

        if (!response.ok) {
            throw new Error(`Failed to create event: ${response.statusText}`);
        }

        const result = await response.json();
        return result.eventId;
    }

    async createClass(eventId, classData) {
        const response = await fetch(`${this.baseUrl}/api/events/${eventId}/classes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(classData),
        });

        if (!response.ok) {
            throw new Error(`Failed to create class: ${response.statusText}`);
        }

        const result = await response.json();
        return result.classId;
    }

    async recordRun(classId, runDetails) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/runs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(runDetails),
        });

        if (!response.ok) {
            throw new Error(`Failed to record run: ${response.statusText}`);
        }

        return response.json();
    }

    async getClassLeaderboard(classId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/leaderboard`);
        if (!response.ok) {
            throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
        }
        return response.json();
    }

    async createEventState() {
        const response = await fetch(`${this.baseUrl}/api/event-state`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to create event state: ${response.statusText}`);
        }

        const result = await response.json();
        return result.eventStateId;
    }

    /**
     * Gets or creates the EventStateDO for a specific event.
     * This ensures each event has exactly one EventStateDO.
     */
    async getOrCreateEventState(eventId) {
        const response = await fetch(`${this.baseUrl}/api/events/${eventId}/event-state`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to get event state: ${response.statusText}`);
        }

        const result = await response.json();
        return result.eventStateId;
    }

    async startEvent(eventStateId, eventId) {
        const response = await fetch(`${this.baseUrl}/api/event-state/${eventStateId}/start`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ eventId }),
        });

        if (!response.ok) {
            throw new Error(`Failed to start event: ${response.statusText}`);
        }

        return response.json();
    }

    async stopEvent(eventStateId) {
        const response = await fetch(`${this.baseUrl}/api/event-state/${eventStateId}/stop`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to stop event: ${response.statusText}`);
        }

        return response.json();
    }

    async getEventStatus(eventStateId) {
        const response = await fetch(`${this.baseUrl}/api/event-state/${eventStateId}/status`);
        if (!response.ok) {
            throw new Error(`Failed to fetch event status: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Validates if an event state exists and is accessible.
     * Returns true if the event state exists, false otherwise.
     */
    async validateEventState(eventStateId) {
        try {
            await this.getEventStatus(eventStateId);
            return true;
        } catch (error) {
            return false;
        }
    }

    async startNextClass(eventStateId) {
        const response = await fetch(`${this.baseUrl}/api/event-state/${eventStateId}/next-class`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to start next class: ${response.statusText}`);
        }

        return response.json();
    }

    async startNextParticipant(eventStateId) {
        const response = await fetch(`${this.baseUrl}/api/event-state/${eventStateId}/next-participant`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to start next participant: ${response.statusText}`);
        }

        return response.json();
    }

    async completeRun(eventStateId, runData) {
        const response = await fetch(`${this.baseUrl}/api/event-state/${eventStateId}/complete-run`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(runData),
        });

        if (!response.ok) {
            throw new Error(`Failed to complete run: ${response.statusText}`);
        }

        return response.json();
    }

    // Report Generation Methods
    async generateClassReport(classId, format = 'json') {
        const response = await fetch(`${this.baseUrl}/api/reports/class/${classId}?format=${format}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to generate class report: ${response.statusText}`);
        }

        if (format === 'csv') {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'class_report.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return { success: true, message: 'Report downloaded successfully' };
        }

        return response.json();
    }

    async generateEventReport(eventId, format = 'json') {
        const response = await fetch(`${this.baseUrl}/api/reports/event/${eventId}?format=${format}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to generate event report: ${response.statusText}`);
        }

        if (format === 'csv') {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'event_report.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return { success: true, message: 'Report downloaded successfully' };
        }

        return response.json();
    }

    async getClasses(eventId) {
        const response = await fetch(`${this.baseUrl}/api/events/${eventId}/classes`);
        if (!response.ok) {
            throw new Error(`Failed to fetch classes: ${response.statusText}`);
        }
        const classesData = await response.json();

        // Convert the classes object to an array format for easier handling
        return Object.entries(classesData).map(([name, id]) => ({
            id,
            name
        }));
    }

    // Participant Management Methods
    async createParticipant(participantDetails) {
        console.log("CREATING PARTICIPANT", participantDetails)
        const response = await fetch(`${this.baseUrl}/api/participants`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(participantDetails),
        });

        if (!response.ok) {
            throw new Error(`Failed to create participant: ${response.statusText}`);
        }

        const result = await response.json();
        return result.participantId;
    }

    async getParticipant(participantId) {
        const response = await fetch(`${this.baseUrl}/api/participants/${participantId}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch participant: ${response.statusText}`);
        }
        return response.json();
    }

    async getParticipants(classId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/participants`);
        if (!response.ok) {
            throw new Error(`Failed to fetch participants: ${response.statusText}`);
        }
        return response.json();
    }

    async addHorseToParticipant(participantId, horseName) {
        const response = await fetch(`${this.baseUrl}/api/participants/${participantId}/horses`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ horseName }),
        });

        if (!response.ok) {
            throw new Error(`Failed to add horse: ${response.statusText}`);
        }

        // Check if response is JSON or plain text
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        } else {
            // Handle plain text response
            const text = await response.text();
            return { message: text };
        }
    }

    async addParticipantToClass(classId, participantId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/participants`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ participantId }),
        });

        if (!response.ok) {
            throw new Error(`Failed to add participant to class: ${response.statusText}`);
        }

        // Check if response is JSON or plain text
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        } else {
            // Handle plain text response
            const text = await response.text();
            return { message: text };
        }
    }

    async removeParticipantFromEvent(eventId, participantId) {
        const response = await fetch(`${this.baseUrl}/api/events/${eventId}/participants/${participantId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to remove participant from event: ${response.statusText}`);
        }

        return response.json();
    }

    async deleteParticipant(participantId) {
        const response = await fetch(`${this.baseUrl}/api/participants/${participantId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to delete participant: ${response.statusText}`);
        }

        return response.json();
    }

    /**
     * Connects to a class's WebSocket to receive leaderboard updates.
     */
    connectToClass(classId) {
        const ws = new WebSocket(`${getWebSocketBaseUrl()}/api/classes/${classId}`);
        ws.addEventListener('error', (error) => {
            console.error('WebSocket error:', error);
        });
        return ws;
    }

    /**
     * Connects to the event state WebSocket.
     */
    connectToEventState(eventStateId) {
        const ws = new WebSocket(`${getWebSocketBaseUrl()}/api/event-state/${eventStateId}`);
        ws.addEventListener('error', (error) => {
            console.error('WebSocket error:', error);
        });
        return ws;
    }

    // Signup Methods
    async calculateSignupCost(classEntries) {
        const response = await fetch(`${this.baseUrl}/api/signups/calculate-cost`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ classEntries }),
        });

        if (!response.ok) {
            throw new Error(`Failed to calculate cost: ${response.statusText}`);
        }

        return response.json();
    }

    async createSignup(signupData) {
        const response = await fetch(`${this.baseUrl}/api/signups`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(signupData),
        });

        if (!response.ok) {
            throw new Error(`Failed to create signup: ${response.statusText}`);
        }

        return response.json();
    }

    async getSignup(signupId) {
        const response = await fetch(`${this.baseUrl}/api/signups/${signupId}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch signup: ${response.statusText}`);
        }
        return response.json();
    }

    async updateSignupPaymentStatus(confirmationId, isPaid) {
        const response = await fetch(`${this.baseUrl}/api/signups/update-payment/${confirmationId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ isPaid }),
        });

        if (!response.ok) {
            throw new Error(`Failed to update signup: ${response.statusText}`);
        }

        return response.json();
    }

    async searchSignupByConfirmationId(confirmationId) {
        const response = await fetch(`${this.baseUrl}/api/signups/search/${confirmationId}`);
        if (!response.ok) {
            throw new Error(`Failed to find signup: ${response.statusText}`);
        }
        return response.json();
    }

    async getClassDetails(classId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/details`);
        if (!response.ok) {
            throw new Error(`Failed to fetch class details: ${response.statusText}`);
        }
        return response.json();
    }

    // Bulk upload methods for setup script
    async bulkCreateClasses(eventId, classes) {
        const response = await fetch(`${this.baseUrl}/api/bulk/classes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ eventId, classes }),
        });

        if (!response.ok) {
            throw new Error(`Failed to bulk create classes: ${response.statusText}`);
        }

        return response.json();
    }

    async bulkAddParticipants(classId, participants) {
        const response = await fetch(`${this.baseUrl}/api/bulk/participants`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ classId, participants }),
        });

        if (!response.ok) {
            throw new Error(`Failed to bulk add participants: ${response.statusText}`);
        }

        return response.json();
    }

    // Draw Management Methods
    async getClassDraw(classId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/draw`);
        if (!response.ok) {
            throw new Error(`Failed to fetch class draw: ${response.statusText}`);
        }
        return response.json();
    }

    async generateClassDraw(classId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/generate-draw`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to generate class draw: ${response.statusText}`);
        }

        return response.json();
    }

    async clearClassDraw(classId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/clear-draw`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to clear class draw: ${response.statusText}`);
        }

        return response.json();
    }

    async scratchParticipant(classId, participantId) {
        const response = await fetch(`${this.baseUrl}/api/classes/${classId}/scratch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ participantId }),
        });

        if (!response.ok) {
            throw new Error(`Failed to scratch participant: ${response.statusText}`);
        }

        return response.json();
    }
}

// Export a singleton instance of the Api class
export const api = new Api(); 